import {
  type ParsedStreamData,
  ParsedStreamDataSchema,
  type StreamError,
  type TextDelta,
  type ToolCall,
  type ToolInputAvailable,
  type ToolInputError,
  type ToolInputStart,
  type ToolOutputAvailable,
  type ToolOutputError,
  type ToolResult,
} from '@sweetistics/contracts/chat-stream';
import { z } from 'zod';
import { getRecordValue, isRecord } from '@/lib/utils/type-guards';
import { parseOrNull } from '../schema';

const CANONICAL_EVENT_TYPES = new Set<string>([
  'text-delta',
  'tool-call',
  'tool-result',
  'tool-input-start',
  'tool-input-available',
  'tool-input-error',
  'tool-input-delta',
  'tool-output-available',
  'tool-output-error',
  'error',
]);

const readString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

// Type guards for stream events
export function isTextDelta(event: ParsedStreamData): event is TextDelta {
  return event.type === 'text-delta';
}

export function isToolCall(event: ParsedStreamData): event is ToolCall {
  return event.type === 'tool-call';
}

export function isToolResult(event: ParsedStreamData): event is ToolResult {
  return event.type === 'tool-result';
}

export function isToolInputStart(event: ParsedStreamData): event is ToolInputStart {
  return event.type === 'tool-input-start';
}

export function isToolInputAvailable(event: ParsedStreamData): event is ToolInputAvailable {
  return event.type === 'tool-input-available';
}

export function isToolInputError(event: ParsedStreamData): event is ToolInputError {
  return event.type === 'tool-input-error';
}

export function isToolOutputAvailable(event: ParsedStreamData): event is ToolOutputAvailable {
  return event.type === 'tool-output-available';
}

export function isToolOutputError(event: ParsedStreamData): event is ToolOutputError {
  return event.type === 'tool-output-error';
}

export function isStreamError(event: ParsedStreamData): event is StreamError {
  return event.type === 'error';
}

// Safe parsers
export function safeParseStreamData(input: unknown): ParsedStreamData | null {
  return parseOrNull(ParsedStreamDataSchema, input);
}

export function parseStreamDataString(json: string): ParsedStreamData | null {
  try {
    if (isControlFrame(json)) {
      return null;
    }
    const parsed: unknown = JSON.parse(json);
    return parseCanonical(parsed) ?? parseVercelLike(parsed) ?? parseOpenAIChunk(parsed) ?? null;
  } catch {
    return null;
  }
}

function isControlFrame(json: string): boolean {
  return !json || json === ':' || json === '[DONE]';
}

function parseCanonical(parsed: unknown): ParsedStreamData | null {
  if (!isRecord(parsed)) {
    return null;
  }
  const record = parsed;
  const candidateType = getRecordValue(record, 'type');
  if (typeof candidateType !== 'string' || !CANONICAL_EVENT_TYPES.has(candidateType)) {
    return null;
  }
  const result = ParsedStreamDataSchema.safeParse(parsed);
  return result.success ? result.data : null;
}

function parseVercelLike(parsed: unknown): ParsedStreamData | null {
  if (!isRecord(parsed)) {
    return null;
  }
  const record = parsed;
  const typeValue = getRecordValue(record, 'type');
  if (typeof typeValue !== 'string') {
    return null;
  }
  const type = typeValue;

  const ignorable = new Set([
    'start',
    'start-step',
    'finish',
    'end',
    'reasoning-start',
    'reasoning-end',
    'message-start',
    'message-end',
    'tool-call-start',
    'tool-call-end',
  ]);
  if (ignorable.has(type)) {
    return null;
  }

  const dataCandidate = getRecordValue(record, 'data');
  const data = isRecord(dataCandidate) ? dataCandidate : {};
  const dataContent = readString(getRecordValue(data, 'content'));
  const dataText = readString(getRecordValue(data, 'text'));

  const deltaCandidate = getRecordValue(record, 'delta');
  let deltaText: string | undefined;
  if (typeof deltaCandidate === 'string') {
    deltaText = deltaCandidate;
  } else if (isRecord(deltaCandidate)) {
    const deltaRecord = deltaCandidate;
    deltaText = readString(getRecordValue(deltaRecord, 'text')) ?? readString(getRecordValue(deltaRecord, 'content'));
  }

  const textValue = readString(getRecordValue(record, 'text'));
  const content = dataContent ?? dataText ?? deltaText ?? textValue;
  if (content && content.length > 0) {
    return { type: 'text-delta', textDelta: content };
  }

  if (type.includes('tool')) {
    const nameValue = readString(getRecordValue(record, 'name'));
    const idValue = readString(getRecordValue(record, 'id'));
    if (nameValue && idValue) {
      const argsCandidate = getRecordValue(record, 'args');
      const arguments_ = isRecord(argsCandidate) ? argsCandidate : {};
      return { type: 'tool-call', toolCallId: idValue, toolName: nameValue, args: arguments_ };
    }
    const toolCallIdValue = readString(getRecordValue(record, 'toolCallId'));
    if (toolCallIdValue && Object.hasOwn(record, 'result')) {
      return { type: 'tool-result', toolCallId: toolCallIdValue, result: getRecordValue(record, 'result') };
    }
  }
  return null;
}

function parseOpenAIChunk(parsed: unknown): ParsedStreamData | null {
  if (!isRecord(parsed)) {
    return null;
  }
  const record = parsed;
  const choicesValue = getRecordValue(record, 'choices');
  const choices = Array.isArray(choicesValue) ? choicesValue : [];
  if (choices.length === 0) {
    return null;
  }
  const firstChoice = choices.find((choice): choice is Record<string, unknown> => isRecord(choice));
  if (!firstChoice) {
    return null;
  }
  const deltaCandidate = getRecordValue(firstChoice, 'delta');
  if (!isRecord(deltaCandidate)) {
    return null;
  }
  const deltaRecord = deltaCandidate;
  const content = readString(getRecordValue(deltaRecord, 'content'));
  return content && content.length > 0 ? { type: 'text-delta', textDelta: content } : null;
}

// Normalized payload shape the UI expects for tool results
export type ToolResultPayload = Record<string, unknown> | string | number | null | unknown[];

const ToolArgumentsSchema = z.preprocess(
  (value) => {
    if (typeof value === 'string') {
      try {
        const parsed: unknown = JSON.parse(value);
        if (isRecord(parsed)) {
          return parsed;
        }
      } catch {
        return { input: value } satisfies Record<string, unknown>;
      }
      return { input: value } satisfies Record<string, unknown>;
    }

    if (isRecord(value)) {
      return value;
    }

    return {} satisfies Record<string, unknown>;
  },
  z.record(z.string(), z.unknown())
);

const ToolResultSchema = z.preprocess(
  (value) => {
    if (value === null) {
      return null;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }
    if (Array.isArray(value)) {
      return value as unknown[];
    }
    if (isRecord(value)) {
      return value;
    }
    return null;
  },
  z.union([z.null(), z.string(), z.number(), z.array(z.unknown()), z.object({}).loose()])
);

export function normalizeToolArgs(value: unknown): Record<string, unknown> {
  const parsed = ToolArgumentsSchema.safeParse(value);
  return parsed.success ? parsed.data : {};
}

export function normalizeToolResult(value: unknown): ToolResultPayload {
  const parsed = ToolResultSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
